import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Cake, PartyPopper, Calendar } from 'lucide-react';
import api from '../utils/api';

/**
 * Compact card listing upcoming birthdays + work anniversaries
 * within the next 7 days (configurable). Visible on every dashboard
 * so even admins who never read digest notifications can plan ahead.
 */
export const UpcomingCelebrationsCard = ({ days = 7 }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/celebrations/upcoming?days=${days}`)
      .then(res => { if (alive) setData(res.data); })
      .catch(() => { /* silent — non-blocking widget */ });
    return () => { alive = false; };
  }, [days]);

  if (!data) return null;
  const items = [
    ...(data.birthdays || []).map(b => ({ ...b, kind: 'birthday' })),
    ...(data.anniversaries || []).map(a => ({ ...a, kind: 'anniversary' })),
  ].sort((a, b) => a.days_away - b.days_away);

  if (items.length === 0) return null;

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch { return iso; }
  };

  const dayLabel = (n) => n === 1 ? 'tomorrow' : `in ${n} days`;

  return (
    <Card className="border-pink-100 shadow-sm mb-4 bg-gradient-to-br from-pink-50/40 to-amber-50/40" data-testid="upcoming-celebrations-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-800">
          <Calendar className="h-4 w-4 text-pink-600" />
          Coming up this week
          <span className="text-xs font-normal text-slate-400">({items.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-pink-100/60">
          {items.map(item => (
            <div
              key={`${item.kind}-${item.id}-${item.event_date}`}
              className="flex items-center gap-3 px-5 py-2 hover:bg-white/60 transition-colors"
              data-testid={`upcoming-${item.kind}-${item.id}`}
            >
              <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                item.kind === 'birthday' ? 'bg-pink-100' : 'bg-amber-100'
              }`}>
                {item.kind === 'birthday'
                  ? <Cake className="h-3.5 w-3.5 text-pink-600" />
                  : <PartyPopper className="h-3.5 w-3.5 text-amber-600" />
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">
                  {item.full_name}
                  {item.kind === 'anniversary' && (
                    <span className="ml-1.5 text-xs text-amber-700 font-semibold">{item.years}-yr</span>
                  )}
                </p>
                <p className="text-xs text-slate-500">
                  {item.kind === 'birthday' ? 'Birthday' : 'Work anniversary'} • {formatDate(item.event_date)}
                </p>
              </div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                item.days_away === 1 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'
              }`}>
                {dayLabel(item.days_away)}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
